// Pattern: docs/design-patterns/frontend/component-patterns.md (display-components-props-only)
// Pattern: docs/design-patterns/frontend/accessibility.md (aria-current, aria-label, tooltip on focus)
// Pattern: docs/design-patterns/anti-patterns/boolean-blindness.md (named `collapsed`, not positional)
// Pattern: docs/design-patterns/common/dry-principle.md (one renderer, shared by the rail and the drawer)
//
// Presentational nav list: sections -> items. Props in, JSX out — no store
// access; the only hook is useLocation(), the same router-state read every
// nav component in this codebase already uses for active-item highlighting
// (Header.tsx, MenuMobile.tsx, NavDropdown.tsx, SidebarLink.tsx). Uses a
// plain `Link` (not `NavLink`) and computes `isActive` itself, because
// NavLink's built-in aria-current only ever reflects a match against `to` —
// it cannot honor `activeMatch` when that differs from `to` (e.g. Settings
// links to /settings/profile but must stay active on any /settings/* route).
import type { JSX } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';

import { Tooltip } from '~/components/Tooltip';

import { isNavItemActive } from './navConfig';
import type { NavItemId, NavSection } from './navConfig';

export interface SidebarNavProps {
  /** Nav sections to render, in order. */
  sections: NavSection[];
  /** Icon-only rail mode: hides labels/section titles, shows tooltips. */
  collapsed: boolean;
  /** Optional per-item count badge (e.g. pending invitations). */
  counts?: Partial<Record<NavItemId, number>>;
  /** Invoked after a nav item is activated — e.g. to close the mobile drawer. */
  onNavigate?: () => void;
}

export function SidebarNav({
  collapsed,
  counts,
  onNavigate,
  sections,
}: SidebarNavProps): JSX.Element {
  const { pathname } = useLocation();

  return (
    <div className="flex flex-col gap-1">
      {sections.map((section, sectionIndex) => (
        <div key={section.id} className="flex flex-col gap-0.5">
          {collapsed && sectionIndex > 0 && (
            <div className="mx-3 my-2 border-t border-default" aria-hidden="true" />
          )}
          {!collapsed && (
            <h3 className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-primary">
              {section.title}
            </h3>
          )}
          <ul className="flex flex-col gap-0.5">
            {section.items.map(item => {
              const active = isNavItemActive(item, pathname);
              const count = counts?.[item.id];

              const link = (
                <Link
                  to={item.to}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  aria-label={collapsed ? item.label : undefined}
                  className={clsx(
                    'relative mx-2 flex min-h-11 items-center gap-3 rounded-md border-l-2 px-2.5 py-2 text-sm transition-colors duration-150',
                    collapsed && 'justify-center px-0',
                    active
                      ? 'border-primary bg-nav-active-bg font-medium text-nav-active-text'
                      : 'border-transparent text-nav-text hover:bg-surface-elevated hover:text-foreground',
                  )}
                >
                  <span
                    className={clsx(
                      'relative shrink-0',
                      active ? 'text-nav-active-text' : 'text-primary',
                    )}
                  >
                    {item.icon}
                    {collapsed && count !== undefined && (
                      <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
                        {count}
                      </span>
                    )}
                  </span>
                  {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                  {!collapsed && count !== undefined && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {count}
                    </span>
                  )}
                </Link>
              );

              return (
                <li key={item.id}>
                  {collapsed ? (
                    <Tooltip content={item.label} placement="right">
                      {link}
                    </Tooltip>
                  ) : (
                    link
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
